import { c as _c } from "react/compiler-runtime";
import { IcoFileText16 } from '@onefootprint/icons';
import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import { NotionEditor, TextArea } from '@onefootprint/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InlineEditableText from '@/components/inline-editable-text';
import { useSkillEditor } from '../hooks/use-skill-editor';
import { joinSkillMarkdown, needsMarkdownSource, readDescription, splitSkillMarkdown, writeDescription } from '../utils/skill-markdown';
type SkillDocumentProps = {
  node: Extract<SkillFileNode, {
    kind: 'file';
  }>;
  path: string;
};
const SkillDocument = t0 => {
  const $ = _c(43);
  const {
    node,
    path
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.skills"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    actions,
    isReadOnly,
    commit
  } = useSkillEditor();
  const [isNewFile] = useState(!node.name);
  const isMarkdown = !node.name || node.name.toLowerCase().endsWith(".md");
  const {
    frontmatter,
    body
  } = splitSkillMarkdown(node.content);
  const [sourceOnly] = useState(() => isMarkdown && needsMarkdownSource(body));
  const [metadataDraft, setMetadataDraft] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState(null);
  const isSkillFile = node.name === "SKILL.md";
  let t2;
  if ($[1] !== actions || $[2] !== commit || $[3] !== isNewFile || $[4] !== node.name || $[5] !== path) {
    t2 = () => {
      if (!isNewFile || !node.name || /\.[^.]+$/.test(node.name)) {
        commit();
        return true;
      }
      actions.rename(path, `${node.name}.md`, {
        save: true
      });
      return true;
    };
    $[1] = actions;
    $[2] = commit;
    $[3] = isNewFile;
    $[4] = node.name;
    $[5] = path;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const saveName = t2;
  const saveFrontmatter = next => {
    if (isReadOnly || next === frontmatter) {
      return;
    }
    actions.setContent(path, joinSkillMarkdown(next, body), {
      save: true
    });
  };
  const t3 = "flex min-h-0 min-w-0 flex-col";
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IcoFileText16 className="shrink-0 text-tertiary" />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== actions || $[9] !== isReadOnly || $[10] !== node.name || $[11] !== path || $[12] !== saveName || $[13] !== t) {
    t5 = <div className="flex h-12 min-w-0 shrink-0 items-center gap-2 border-b border-tertiary px-5">{t4}{node.name === "SKILL.md" ? <span className="text-label-3 text-primary">{node.name}</span> : <div className="min-w-0 flex-1"><InlineEditableText value={node.name} fallback={t(_temp)} placeholder={t(_temp2)} ariaLabel={t(_temp3)} shouldCloseOnBlur={saveName} canEdit={!isReadOnly} autoFocus={!node.name && !isReadOnly} onChange={name => actions.rename(path, name)} buttonClassName="max-w-full px-0" textClassName="truncate text-label-3" inputClassName="text-label-3" /></div>}</div>;
    $[8] = actions;
    $[9] = isReadOnly;
    $[10] = node.name;
    $[11] = path;
    $[12] = saveName;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = "min-h-0 flex-1 overflow-y-auto overscroll-contain";
  const t7 = "mx-auto w-full max-w-[800px] px-6 py-8 @min-[800px]:px-12 @min-[800px]:py-10";
  const t8 = isMarkdown && !sourceOnly && isSkillFile && <div className="mb-8"><TextArea label={t(_temp4)} hint={t(_temp5)} placeholder={t(_temp6)} value={descriptionDraft ?? readDescription(frontmatter)} onChangeText={setDescriptionDraft} onBlur={() => {
      if (descriptionDraft !== null) {
        saveFrontmatter(writeDescription(frontmatter, descriptionDraft));
      }
      setDescriptionDraft(null);
    }} readOnly={isReadOnly} rows={2} className="w-full" /></div>;
  let t9;
  if ($[15] !== frontmatter || $[16] !== isMarkdown || $[17] !== isReadOnly || $[18] !== isSkillFile || $[19] !== metadataDraft || $[20] !== saveFrontmatter || $[21] !== setMetadataDraft || $[22] !== sourceOnly || $[23] !== t) {
    t9 = isMarkdown && !sourceOnly && !isSkillFile && frontmatter && <details className="mb-6 text-caption-1 text-tertiary"><summary className="w-fit cursor-pointer rounded focus-visible:outline-2 focus-visible:outline-accent">{t(_temp7)}</summary><TextArea aria-label={t(_temp8)} value={metadataDraft ?? frontmatter} onChangeText={setMetadataDraft} onBlur={() => {
        if (metadataDraft !== null) {
          saveFrontmatter(metadataDraft);
        }
        setMetadataDraft(null);
      }} readOnly={isReadOnly} className="mt-3 min-h-32! w-full text-snippet-1" /></details>;
    $[15] = frontmatter;
    $[16] = isMarkdown;
    $[17] = isReadOnly;
    $[18] = isSkillFile;
    $[19] = metadataDraft;
    $[20] = saveFrontmatter;
    $[21] = setMetadataDraft;
    $[22] = sourceOnly;
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== actions || $[26] !== body || $[27] !== commit || $[28] !== frontmatter || $[29] !== isMarkdown || $[30] !== isReadOnly || $[31] !== node.content || $[32] !== path || $[33] !== sourceOnly || $[34] !== t) {
    t10 = isMarkdown && !sourceOnly ? <fieldset aria-label={t(_temp9)} className="min-h-96 [&_.tiptap]:min-h-96">{isReadOnly && !body ? <p className="text-body-2 text-tertiary">{t(_temp10)}</p> : <NotionEditor content={body} onChange={content => actions.setContent(path, joinSkillMarkdown(frontmatter, content))} onBlur={commit} editable={!isReadOnly} emitOnMount={false} placeholder={t(_temp11)} />}</fieldset> : <>{isMarkdown && sourceOnly && <p className="mb-4 text-caption-1 text-tertiary">{t(_temp12)}</p>}<TextArea aria-label={t(_temp13)} value={node.content} onChangeText={content_0 => actions.setContent(path, content_0)} onBlur={commit} readOnly={isReadOnly} placeholder={t(_temp14)} className="min-h-96! w-full resize-y! border-0! bg-transparent! p-0! text-snippet-1 leading-relaxed! shadow-none!" /></>;
    $[25] = actions;
    $[26] = body;
    $[27] = commit;
    $[28] = frontmatter;
    $[29] = isMarkdown;
    $[30] = isReadOnly;
    $[31] = node.content;
    $[32] = path;
    $[33] = sourceOnly;
    $[34] = t;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t8 || $[38] !== t9) {
    t11 = <div className={t6}><div className={t7}>{t8}{t9}{t10}</div></div>;
    $[36] = t10;
    $[37] = t8;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t11 || $[41] !== t5) {
    t12 = <section className={t3}>{t5}{t11}</section>;
    $[40] = t11;
    $[41] = t5;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
};
export default SkillDocument;
function _temp($) {
  return $.newFile;
}
function _temp2($_0) {
  return $_0.fileName;
}
function _temp3($_1) {
  return $_1.fileName;
}
function _temp4($_2) {
  return $_2.descriptionField.label;
}
function _temp5($_3) {
  return $_3.descriptionField.hint;
}
function _temp6($_4) {
  return $_4.descriptionField.placeholder;
}
function _temp7($_5) {
  return $_5.metadata;
}
function _temp8($_6) {
  return $_6.metadata;
}
function _temp9($_7) {
  return $_7.contentLabel;
}
function _temp10($_8) {
  return $_8.emptyFile;
}
function _temp11($_9) {
  return $_9.contentField.placeholder;
}
function _temp12($_10) {
  return $_10.markdownSource;
}
function _temp13($_11) {
  return $_11.contentLabel;
}
function _temp14($_12) {
  return $_12.contentField.placeholder;
}